import type { jsPDF } from 'jspdf';
import dayjs from 'dayjs';
import { IFamilyMember } from '../../caremap/ICareMap';
import { Logger } from '../../support/Logger';
import { ISO8601 } from '../../support/ISO8601';

// US Letter portrait in points, which is jsPDF's native unit for the 'letter' format.
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const PAGE_CENTER = PAGE_WIDTH / 2;
const PAGE_MARGIN = 56;
const CONTENT_WIDTH = PAGE_WIDTH - (PAGE_MARGIN * 2);

const BRAND_PRIMARY: [number, number, number] = [17, 120, 157]; // --ion-color-primary #11789D
const BODY_GREY: [number, number, number] = [80, 80, 80];
const FOOTER_GREY: [number, number, number] = [140, 140, 140];

const LOGO_URL = '/assets/images/logos/logo-with-text-primary.svg';
const LOGO_INTRINSIC_WIDTH = 360;
const LOGO_INTRINSIC_HEIGHT = 70;
const LOGO_WIDTH = 190;
const LOGO_HEIGHT = LOGO_WIDTH * (LOGO_INTRINSIC_HEIGHT / LOGO_INTRINSIC_WIDTH);

// Rasterize the logo well above its printed size so it stays crisp at 300dpi.
const LOGO_RASTER_SCALE = 4;

const AVATAR_SIZE = 68;
const AVATAR_RASTER_PIXELS = 320;
// Generous space between the CARE CODE label and the photo. The label reads as belonging to the
// logo above it, so it sits close to the logo and well clear of the care recipient below.
const AVATAR_GAP = 40;

// Distance from the bottom of the logo to the CARE CODE baseline. Tighter when a photo follows,
// so the label groups with the logo rather than floating between the two.
const LABEL_GAP = 39;
const LABEL_GAP_WITH_PHOTO = 24;

/**
 * jsPDF's built-in Helvetica is one of the standard 14 PDF fonts and can only encode cp1252, so a
 * patient name like Nguyễn or Волков prints as wrong glyphs. Noto Sans is embedded to cover the
 * Latin, Vietnamese, Cyrillic, Greek and Turkish ranges. It does NOT cover CJK or Arabic; those
 * names still fall back to whatever Helvetica does with them.
 */
const FONT_FAMILY = 'NotoSans';
const FALLBACK_FONT_FAMILY = 'helvetica';
const FONT_FILES = [
    { file: 'NotoSans-Regular.ttf', url: '/assets/fonts/NotoSans-Regular.ttf', style: 'normal' },
    { file: 'NotoSans-Bold.ttf', url: '/assets/fonts/NotoSans-Bold.ttf', style: 'bold' }
];

const QR_SIZE = 288;
// A photo adds a band above the heading, so the code gives back the height it needs. Still
// comfortably large to scan.
const QR_SIZE_WITH_PHOTO = 258;
// Source pixels for the QR bitmap. 1200px across 288pt is well beyond 300dpi once printed.
const QR_SOURCE_PIXELS = 1200;

const HEADING_SIZE = 26;
// Below this the heading stops looking like a heading, so wrap onto another line instead.
const HEADING_MIN_SIZE = 16;

// 'H' (30% recovery) is deliberate: this sheet is printed and lives on a fridge or a
// doorframe, so it has to survive creases, smudges and partial shadow. The URL is short
// enough that the extra recovery data doesn't shrink the modules below easy scanning size.
const QR_ERROR_CORRECTION = 'H';

// The brand teal is dark enough against white to clear the contrast a scanner needs, and only
// the dark modules are tinted so the quiet zone stays pure white.
const QR_DARK = '#11789D';
const QR_LIGHT = '#FFFFFF';

// jsPDF embeds bitmaps uncompressed unless told otherwise; without this the sheet is ~6MB
// because of the logo, which makes it painful to share from a phone.
const IMAGE_COMPRESSION = 'FAST';

export interface ICareCodeDocumentInput {
    viewerUrl: string;
    patient: Partial<IFamilyMember>;
    expiresAt: ISO8601;
}

export class CareCodeDocumentBuilder {
    static inject = () => [Logger];
    constructor(private logger: Logger) {}

    private logo: string | undefined;
    private fonts: { file: string; style: string; base64: string; }[] | undefined;
    private avatar: string | undefined;

    /**
     * Loads everything the sheet needs from the network or the canvas, and caches it. Callers do
     * this before minting a viewer token so the failure-prone work happens while there is still
     * nothing to lose: a token, once minted, cannot be revoked, so anything that can throw
     * belongs ahead of it.
     */
    prepareAssets = async ({ patient }: { patient: Partial<IFamilyMember>; }): Promise<void> => {
        await Promise.all([this.prepareLogo(), this.prepareFonts(), this.prepareAvatar(patient)]);
    };

    private prepareLogo = async () => {
        if (this.logo) {
            return;
        }

        try {
            this.logo = await this.rasterizeSvg({
                url: LOGO_URL,
                width: LOGO_INTRINSIC_WIDTH * LOGO_RASTER_SCALE,
                height: LOGO_INTRINSIC_HEIGHT * LOGO_RASTER_SCALE
            });
        } catch (err: any) {
            // A sheet without the logo is still a working Care Code, so a missing or
            // unrasterizable brand asset must not cost the user their code. Report it
            // instead of failing the whole document.
            this.logger.error(err, { context: `CareCodeDocumentBuilder: could not rasterize ${LOGO_URL}` });
        }
    };

    private prepareFonts = async () => {
        if (this.fonts) {
            return;
        }

        try {
            this.fonts = await Promise.all(FONT_FILES.map(async ({ file, url, style }) => ({
                file,
                style,
                base64: await this.fetchAsBase64(url)
            })));
        } catch (err: any) {
            // Falling back to Helvetica still produces a correct sheet for the cp1252 names that
            // are the common case, so a font that won't load must not cost the user their code.
            this.logger.error(err, { context: 'CareCodeDocumentBuilder: could not load the embedded font' });
        }
    };

    /**
     * Only a photo the user actually uploaded earns a place on the sheet. A stock avatar, a colour
     * or initials say nothing a reader of a printed page needs, so those fall through to the
     * plain layout rather than filling the space with a placeholder.
     */
    private prepareAvatar = async (patient: Partial<IFamilyMember>) => {
        this.avatar = undefined;

        // photoDataURI is already inline, so it needs no network round trip and cannot be
        // blocked by CORS the way a remote photo can.
        const source = patient.photoDataURI || patient.photo;
        if (!source) {
            return;
        }

        try {
            this.avatar = await this.rasterizeToCircle(source);
        } catch (err: any) {
            // Falls through to the layout used when there is no photo at all.
            this.logger.error(err, { context: 'CareCodeDocumentBuilder: could not load the care recipient photo' });
        }
    };

    build = async ({ viewerUrl, patient, expiresAt }: ICareCodeDocumentInput): Promise<Blob> => {
        // Loaded on demand so jsPDF and qrcode stay out of the app's first-load bundle; the
        // Sharing sheet that reaches this class is mounted globally.
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait', compress: true });

        await this.prepareAssets({ patient });
        this.registerFonts(doc);

        const firstName = patient.firstName || 'your loved one';

        // A running cursor rather than fixed offsets, so a heading that wraps pushes what follows
        // down instead of colliding with it. The photo needs the page to start higher to leave
        // room for the band it adds.
        let y = this.avatar ? 44 : 64;
        y = this.drawLogo(doc, y);
        y = this.drawLabel(doc, y + (this.avatar ? LABEL_GAP_WITH_PHOTO : LABEL_GAP));
        y = this.drawAvatar(doc, y);
        y = this.drawHeading({ doc, firstName, y: y + 30 });
        y = await this.drawQrCode({ doc, viewerUrl, y: y + 18 });
        this.drawInstructions({ doc, firstName, expiresAt, y: y + 34 });

        return doc.output('blob');
    };

    private registerFonts = (doc: jsPDF) => {
        for (const { file, style, base64 } of this.fonts || []) {
            doc.addFileToVFS(file, base64);
            doc.addFont(file, FONT_FAMILY, style);
        }
    };

    private useFont = (doc: jsPDF, style: 'normal' | 'bold') => {
        doc.setFont(this.fonts ? FONT_FAMILY : FALLBACK_FONT_FAMILY, style);
    };

    private drawLogo = (doc: jsPDF, y: number) => {
        if (!this.logo) {
            return y;
        }

        doc.addImage(this.logo, 'PNG', PAGE_CENTER - (LOGO_WIDTH / 2), y, LOGO_WIDTH, LOGO_HEIGHT, 'care-code-logo', IMAGE_COMPRESSION);
        return y + LOGO_HEIGHT;
    };

    private drawLabel = (doc: jsPDF, y: number) => {
        this.useFont(doc, 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...BRAND_PRIMARY);
        doc.text('CARE CODE', PAGE_CENTER, y, { align: 'center', charSpace: 2 });
        return y;
    };

    private drawAvatar = (doc: jsPDF, y: number) => {
        if (!this.avatar) {
            // Leaves the cursor untouched so the plain layout closes up rather than holding a gap.
            return y;
        }

        doc.addImage(this.avatar, 'PNG', PAGE_CENTER - (AVATAR_SIZE / 2), y + AVATAR_GAP, AVATAR_SIZE, AVATAR_SIZE, 'care-code-avatar', IMAGE_COMPRESSION);
        return y + AVATAR_GAP + AVATAR_SIZE;
    };

    private drawHeading = ({ doc, firstName, y }: { doc: jsPDF; firstName: string; y: number; }) => {
        this.useFont(doc, 'bold');
        doc.setTextColor(0, 0, 0);
        let cursor = this.drawFittedHeading(doc, `Check in on ${firstName}`, y);

        this.useFont(doc, 'normal');
        doc.setFontSize(13);
        doc.setTextColor(...BODY_GREY);
        cursor += 22;
        doc.text('Scan this code with your phone\u2019s camera', PAGE_CENTER, cursor, { align: 'center' });

        return cursor;
    };

    /**
     * The name is free text, so it can be far longer than a first name. Shrink to fit, then wrap,
     * rather than letting jsPDF run the heading off both edges of a page someone is about to print.
     */
    private drawFittedHeading = (doc: jsPDF, heading: string, y: number) => {
        let size = HEADING_SIZE;
        doc.setFontSize(size);
        while (size > HEADING_MIN_SIZE && doc.getTextWidth(heading) > CONTENT_WIDTH) {
            size -= 1;
            doc.setFontSize(size);
        }

        const lines: string[] = doc.splitTextToSize(heading, CONTENT_WIDTH);
        const lineHeight = size * 1.15;
        lines.forEach((line, index) => {
            doc.text(line, PAGE_CENTER, y + (index * lineHeight), { align: 'center' });
        });

        return y + ((lines.length - 1) * lineHeight);
    };

    private drawQrCode = async ({ doc, viewerUrl, y }: { doc: jsPDF; viewerUrl: string; y: number; }) => {
        const QRCode = (await import('qrcode')).default;

        const qr = await QRCode.toDataURL(viewerUrl, {
            errorCorrectionLevel: QR_ERROR_CORRECTION,
            margin: 1,
            width: QR_SOURCE_PIXELS,
            color: { dark: QR_DARK, light: QR_LIGHT }
        });

        const size = this.avatar ? QR_SIZE_WITH_PHOTO : QR_SIZE;
        doc.addImage(qr, 'PNG', PAGE_CENTER - (size / 2), y, size, size, 'care-code-qr', IMAGE_COMPRESSION);
        return y + size;
    };

    private drawInstructions = ({ doc, firstName, expiresAt, y }: { doc: jsPDF; firstName: string; expiresAt: ISO8601; y: number; }) => {
        this.useFont(doc, 'bold');
        doc.setFontSize(13);
        doc.setTextColor(0, 0, 0);
        doc.text('How it works:', PAGE_CENTER, y, { align: 'center' });

        this.useFont(doc, 'normal');
        doc.setFontSize(12);
        doc.setTextColor(...BODY_GREY);
        const steps = [
            '1.  Open the camera app on your phone',
            '2.  Point it at the code above',
            '3.  Tap the link that appears'
        ];
        steps.forEach((step, index) => {
            doc.text(step, PAGE_CENTER, y + 26 + (index * 20), { align: 'center' });
        });

        const dividerY = y + 108;
        doc.setDrawColor(220, 220, 220);
        doc.line(140, dividerY, PAGE_WIDTH - 140, dividerY);

        this.useFont(doc, 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('No app or account needed!', PAGE_CENTER, dividerY + 24, { align: 'center' });

        this.useFont(doc, 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...BODY_GREY);
        const summary: string[] = doc.splitTextToSize(`You'll see a view-only summary of how ${firstName} is doing.`, CONTENT_WIDTH);
        doc.text(summary[0], PAGE_CENTER, dividerY + 44, { align: 'center' });
        doc.text(`This code stops working on ${dayjs(expiresAt).format('MMMM D, YYYY')}.`, PAGE_CENTER, dividerY + 62, { align: 'center' });

        doc.setFontSize(10);
        doc.setTextColor(...FOOTER_GREY);
        doc.text('www.innerhive.com', PAGE_CENTER, PAGE_HEIGHT - 30, { align: 'center' });
    };



    /**
     * Draws the image into a circular clip so the sheet shows the same round avatar the app does,
     * rather than a square crop. jsPDF has no image masking, so the rounding happens on canvas.
     */
    private rasterizeToCircle = async (src: string): Promise<string> => {
        const image = await this.loadImage(src);

        const canvas = window.document.createElement('canvas');
        canvas.width = AVATAR_RASTER_PIXELS;
        canvas.height = AVATAR_RASTER_PIXELS;

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('could not get a 2d canvas context');
        }

        const radius = AVATAR_RASTER_PIXELS / 2;
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, AVATAR_RASTER_PIXELS, AVATAR_RASTER_PIXELS);
        context.save();
        context.beginPath();
        context.arc(radius, radius, radius, 0, Math.PI * 2);
        context.clip();

        // Cover the circle without distorting the source, matching the app's background-size: cover.
        const scale = Math.max(AVATAR_RASTER_PIXELS / image.width, AVATAR_RASTER_PIXELS / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        context.drawImage(image, radius - (width / 2), radius - (height / 2), width, height);
        context.restore();

        return canvas.toDataURL('image/png');
    };

    /**
     * jsPDF can only embed bitmaps, so the brand SVG is drawn onto a canvas first. The SVG is
     * same-origin and loaded through a blob URL, which is expected to leave the canvas untainted.
     * On iOS the WebView origin is a custom scheme, where WebKit has historically been stricter
     * about SVG-into-canvas, so treat toDataURL as something that can throw.
     */
    private rasterizeSvg = async ({ url, width, height }: { url: string; width: number; height: number; }): Promise<string> => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`failed to fetch ${url}: ${response.status}`);
        }

        const objectUrl = URL.createObjectURL(await response.blob());
        try {
            const image = await this.loadImage(objectUrl);

            const canvas = window.document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');
            if (!context) {
                throw new Error('could not get a 2d canvas context');
            }

            context.drawImage(image, 0, 0, width, height);
            return canvas.toDataURL('image/png');
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    };

    private fetchAsBase64 = async (url: string): Promise<string> => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`failed to fetch ${url}: ${response.status}`);
        }

        const bytes = new Uint8Array(await response.arrayBuffer());

        // Converting in one String.fromCharCode call overflows the argument limit on a font-sized
        // buffer, so build the binary string in chunks before encoding.
        const chunkSize = 8192;
        let binary = '';
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }

        return window.btoa(binary);
    };

    private loadImage = (src: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            // Remote photos need CORS permission before the canvas will export.
            image.crossOrigin = 'anonymous';
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`could not load image ${src}`));
            image.src = src;
        });
    };
}
